/**
 * RSVP data helpers — both sides of the feature.
 *
 * Couple side (authenticated): `ensureRsvpLink` / `syncRsvpTokenDoc` generate
 * a guest's secret token and keep the public `/rsvpTokens/{token}` snapshot
 * fresh. Guest side (unauthenticated): `loadRsvpToken` reads that snapshot to
 * render the public form, and `submitRsvp` writes the response straight back
 * onto the guest doc — Firestore rules gate it on the token.
 */

import { doc, getDoc, serverTimestamp, setDoc, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { reportError } from "@/lib/report-error";
import { toIsoString } from "@/lib/timestamps";
import {
  RsvpTokenSchema,
  type Child,
  type Guest,
  type MealPreference,
  type PlusOne,
  type RsvpToken,
  type Wedding,
} from "@amazing-wedding/shared-types";
import { isWeddingCoupleGuest } from "@/features/guests/guest-unit-rows";

/** Public RSVP link for a token, rooted at the current origin. */
export function rsvpLinkUrl(token: string): string {
  return `${window.location.origin}/rsvp/${token}`;
}

/** Cryptographically-random, URL-safe token — 48 hex chars (24 bytes). */
function generateToken(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Denormalised snapshot the public form needs — everything but the timestamps.
 * Exported for unit tests; all fields are Firestore-safe (no undefined).
 */
export function buildTokenSnapshot(
  token: string,
  wedding: Wedding,
  guest: Guest,
): Omit<RsvpToken, "createdAt" | "updatedAt"> {
  return {
    token,
    weddingId: wedding.id,
    guestId: guest.id,
    firstName: guest.firstName ?? "",
    lastName: guest.lastName ?? "",
    plusOneAllowed: guest.plusOneAllowed ?? false,
    // Mirror existing companion / children so the public form can pre-fill them
    // and a submit doesn't silently wipe what the couple already entered.
    plusOne: guest.plusOne ?? null,
    children: guest.children ?? [],
    coupleName1: wedding.coupleName1 ?? "",
    coupleName2: wedding.coupleName2 ?? "",
    weddingDate: wedding.weddingDate,
    venue: wedding.venue ?? null,
    rsvpMessage: wedding.website?.content?.rsvp ?? null,
    rsvpDeadline: wedding.website?.rsvpDeadline ?? null,
    locale: wedding.settings?.language ?? "pl",
  };
}

/**
 * Ensure the guest has an RSVP token and a fresh `/rsvpTokens` doc, returning
 * the public link. The first call generates the token and writes it onto the
 * guest doc; every call (re)writes the token doc so it reflects current data.
 */
export async function ensureRsvpLink(wedding: Wedding, guest: Guest): Promise<string> {
  if (isWeddingCoupleGuest(guest)) {
    throw new Error("Para młoda nie potrzebuje linku RSVP.");
  }
  const token = guest.rsvpToken ?? generateToken();
  const snapshot = buildTokenSnapshot(token, wedding, guest);
  const ctx = { weddingId: wedding.id, guestId: guest.id, operation: "ensureRsvpLink" };

  try {
    if (!guest.rsvpToken) {
      const batch = writeBatch(db);
      const guestRef = doc(db, "weddings", wedding.id, "guests", guest.id);
      batch.update(guestRef, { rsvpToken: token, updatedAt: serverTimestamp() });
      batch.set(doc(db, "rsvpTokens", token), {
        ...snapshot,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
    } else {
      await setDoc(
        doc(db, "rsvpTokens", token),
        { ...snapshot, updatedAt: serverTimestamp() },
        { merge: true },
      );
    }
  } catch (err) {
    reportError("rsvp", err, ctx);
    throw err;
  }

  return rsvpLinkUrl(token);
}

/**
 * Refresh the `/rsvpTokens` snapshot after a guest edit so the public form
 * shows current data. No-op when the guest has no token (link never shared).
 */
export async function syncRsvpTokenDoc(wedding: Wedding, guest: Guest): Promise<void> {
  if (!guest.rsvpToken) return;
  const ctx = { weddingId: wedding.id, guestId: guest.id, operation: "syncRsvpTokenDoc" };

  try {
    await setDoc(
      doc(db, "rsvpTokens", guest.rsvpToken),
      { ...buildTokenSnapshot(guest.rsvpToken, wedding, guest), updatedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (err) {
    reportError("rsvp", err, ctx);
    throw err;
  }
}

/** Coerce raw Firestore doc fields before Zod parse (Timestamps → ISO strings). */
export function parseRsvpTokenDoc(token: string, raw: Record<string, unknown>): RsvpToken | null {
  const normalized = {
    ...raw,
    token,
    plusOneAllowed: raw.plusOneAllowed ?? false,
    weddingDate: toIsoString(raw.weddingDate) ?? raw.weddingDate,
    rsvpDeadline:
      raw.rsvpDeadline == null ? null : (toIsoString(raw.rsvpDeadline) ?? raw.rsvpDeadline),
    createdAt: toIsoString(raw.createdAt) ?? raw.createdAt,
    updatedAt: toIsoString(raw.updatedAt) ?? raw.updatedAt,
  };

  const parsed = RsvpTokenSchema.safeParse(normalized);
  if (!parsed.success) {
    reportError("rsvp", parsed.error, { token, operation: "parseRsvpTokenDoc" });
    return null;
  }
  return parsed.data;
}

/** Read the public token snapshot. Returns null if the token is unknown. */
export async function loadRsvpToken(token: string): Promise<RsvpToken | null> {
  const snap = await getDoc(doc(db, "rsvpTokens", token));
  if (!snap.exists()) return null;
  return parseRsvpTokenDoc(token, snap.data() as Record<string, unknown>);
}

/** True once the RSVP deadline has passed — the public form is then closed. */
export function isRsvpClosed(deadline: string | null | undefined, now: Date = new Date()): boolean {
  if (!deadline) return false;
  return now.getTime() > new Date(deadline).getTime();
}

/** The fields a guest sets on the public RSVP form. */
export interface RsvpSubmission {
  rsvpStatus: "accepted" | "declined";
  mealPreference: MealPreference | null;
  attendingCeremony: boolean;
  attendingReception: boolean;
  attendingAfterParty: boolean;
  accommodationRequired: boolean;
  plusOne: PlusOne | null;
  children: Child[];
  notes: string | null;
}

/**
 * Write a guest's RSVP response. `rsvpSubmittedToken` is the proof field the
 * Firestore rule compares against the stored secret — see firestore.rules.
 *
 * Note: we intentionally don't touch `weddings/{id}.attendeeCount` here.
 * The public RSVP path is unauthenticated and Firestore rules deny both the
 * guest-list read and the wedding-doc write to non-members. The count is
 * reconciled by `maybeBackfillAttendeeCount` next time the couple loads
 * their guests view (see lib/firestore/subscriptions/guests.ts).
 */
export async function submitRsvp(token: RsvpToken, submission: RsvpSubmission): Promise<void> {
  const guestRef = doc(db, "weddings", token.weddingId, "guests", token.guestId);
  await updateDoc(guestRef, {
    ...submission,
    rsvpRespondedAt: serverTimestamp(),
    rsvpSubmittedToken: token.token,
    updatedAt: serverTimestamp(),
  });
}
